import { supabase } from './supabaseClient';
import { LinkedProjectConfig } from '../types';
import { LinkedBillingMode } from './linkedBillingMode';
import { PROJECT_LINK_COLUMNS, linkConfigFromRow, type ProjectLinkColumns } from './projectLink';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE SCHREIBWEGE DER VERKNUEPFUNG — und nur diese
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `projects.parent_project_id`, `linked_billing_mode` und
 * `linked_billing_history` (Nr. 71) werden von GENAU DREI Vorgaengen
 * geschrieben: verknuepfen, trennen, Abrechnungsart umstellen. Alle drei
 * stehen hier. `saveProjectToCloud` fasst die Spalten NIE an — genau darin
 * liegt der Schutz: ein veralteter Blob kann die Verknuepfung nicht mehr
 * mitreissen (Vorfall 6290, 02.09.2026).
 *
 * ─── Warum die Zeilenzahl geprueft wird ────────────────────────────────────
 * Fehlende Schreibrechte liefern von PostgREST KEINEN Fehler, sondern null
 * betroffene Zeilen (dasselbe Muster wie in `applyLinkTransfer`). Ein Vorgang,
 * der „ok" meldet und nichts geschrieben hat, waere hier der schlimmste Fall:
 * die Positionen sind umgezogen, die Verknuepfung nicht gesetzt.
 *
 * ─── Was die Datenbank selbst abweist ──────────────────────────────────────
 * Selbstbezug, unbekannte Art, Art ohne Mutter, Ketten in beide Richtungen —
 * das sind CHECKs und ein Trigger aus Nr. 71. Ihre Meldungen kommen im
 * Klartext (deutsch, aus dem Trigger) und werden unveraendert weitergereicht.
 */

const zeilenOderFehler = async (
    abfrage: PromiseLike<{ data: unknown; error: { message?: string } | null }>,
    was: string,
): Promise<void> => {
    const { data, error } = await abfrage;
    if (error) throw new Error(error.message || `${was} fehlgeschlagen.`);
    if (!Array.isArray(data) || data.length === 0) {
        throw new Error(`${was}: Die Projektzeile wurde nicht geschrieben (keine Berechtigung oder Projekt nicht gefunden).`);
    }
};

/** Verknuepfen: die Tochter bekommt ihre Mutter und die Abrechnungsart. */
export const setProjectLink = async (
    childProjectId: string,
    parentProjectId: string,
    mode: LinkedBillingMode,
): Promise<void> => {
    const kind = (childProjectId || '').trim();
    const mutter = (parentProjectId || '').trim();
    if (!kind || kind === 'temp-id' || !mutter) {
        throw new Error('Zum Verknuepfen fehlt die Projektkennung. Bitte das Projekt zuerst speichern.');
    }
    await zeilenOderFehler(
        supabase
            .from('projects')
            .update({ parent_project_id: mutter, linked_billing_mode: mode, linked_billing_history: null })
            .eq('id', kind)
            .select('id'),
        'Verknuepfen',
    );
};

/** Trennen: alle drei Spalten auf NULL. */
export const clearProjectLink = async (childProjectId: string): Promise<void> => {
    const kind = (childProjectId || '').trim();
    if (!kind || kind === 'temp-id') throw new Error('Zum Trennen fehlt die Projektkennung.');
    await zeilenOderFehler(
        supabase
            .from('projects')
            .update({ parent_project_id: null, linked_billing_mode: null, linked_billing_history: null })
            .eq('id', kind)
            .select('id'),
        'Trennen',
    );
};

/**
 * Abrechnungsart umstellen — Art UND Historie, wie `switchBillingMode` sie
 * gerechnet hat. Geschrieben wird nur an einer Zeile, die noch Tochter ist
 * (`parent_project_id is not null`): der CHECK `projects_linked_mode_needs_parent`
 * wiese es sonst ab, aber mit einer Meldung, die niemandem weiterhilft.
 */
export const setLinkedBillingMode = async (
    childProjectId: string,
    config: LinkedProjectConfig,
): Promise<void> => {
    const kind = (childProjectId || '').trim();
    if (!kind || kind === 'temp-id') throw new Error('Zum Umstellen fehlt die Projektkennung.');
    await zeilenOderFehler(
        supabase
            .from('projects')
            .update({
                linked_billing_mode: config.billingMode || 'daily_flat',
                linked_billing_history: config.billingModeHistory ?? null,
            })
            .eq('id', kind)
            .not('parent_project_id', 'is', null)
            .select('id'),
        'Umstellen der Abrechnungsart',
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// LESEN — die Kinder einer Mutter, die Namen der Muetter
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Toechter eines Projekts ueber den Index aus Nr. 71 — statt einer
 * `contains(data, …)`-Suche ueber die ganze Tabelle.
 *
 * Wirft bei einem Abfragefehler; der Aufrufer entscheidet, ob „unbekannt"
 * fuer ihn sperren muss (Archiv-Riegel, Ketten-Riegel) oder leer sein darf
 * (Anzeige).
 */
export const fetchLinkedChildren = async <T = { id: string }>(
    parentProjectId: string,
    columns: string = 'id',
): Promise<T[]> => {
    const id = (parentProjectId || '').trim();
    if (!id || id === 'temp-id') return [];
    const { data, error } = await supabase
        .from('projects')
        .select(columns)
        .eq('parent_project_id', id);
    if (error) throw error;
    return (Array.isArray(data) ? data : []) as T[];
};

/**
 * `projects.name` je Mutter — EIN Aufruf fuer alle, damit die Projektliste
 * nicht je Tochter einen Rundlauf zahlt. Faellt die Abfrage aus, bleibt die
 * Karte leer: der Name ist Anzeige, kein Grund, ein Laden scheitern zu lassen.
 */
export const fetchParentNames = async (parentIds: string[]): Promise<Map<string, string>> => {
    const namen = new Map<string, string>();
    const ids = [...new Set((parentIds || []).filter(Boolean))];
    if (ids.length === 0) return namen;
    try {
        const { data, error } = await supabase.from('projects').select('id, name').in('id', ids);
        if (error) throw error;
        for (const p of (data || []) as Array<{ id: string; name?: string | null }>) {
            if (p.name) namen.set(p.id, p.name);
        }
    } catch (e) {
        console.warn('Name des Mutterprojekts nicht lesbar — die Anzeige bleibt ohne Namen:', e);
    }
    return namen;
};

/**
 * Die Verknuepfung MEHRERER Projekte, aus den Spalten — fuer Aufrufer, die
 * nur das Config brauchen und nicht das ganze Projekt (Abrechnungsart der
 * Schwestern, Journal-Spiegel).
 */
export const fetchLinkConfigs = async (
    projectIds: string[],
): Promise<Map<string, LinkedProjectConfig | undefined>> => {
    const configs = new Map<string, LinkedProjectConfig | undefined>();
    const ids = [...new Set((projectIds || []).filter(Boolean))];
    if (ids.length === 0) return configs;
    const { data, error } = await supabase
        .from('projects')
        .select(['id', ...PROJECT_LINK_COLUMNS].join(', '))
        .in('id', ids);
    if (error) throw error;
    for (const p of (data || []) as unknown as Array<{ id: string } & ProjectLinkColumns>) {
        configs.set(p.id, linkConfigFromRow(p, null));
    }
    return configs;
};
